/**
 * Email Composer Sheet Component
 *
 * Allows users to compose and send emails with PDF attachments via Gmail
 * Uses Pica Gmail integration for sending emails from user's connected account
 */

import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { Mail, Send, Loader2, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";
import {
  generateEmailSubject,
  validateEmail,
  validateMultipleEmails,
  preparePdfAttachment,
  formatFileSize,
  generateDefaultEmailBody,
} from "@/lib/emailUtils";
import { Link } from "react-router-dom";

interface EmailComposerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: Id<"documents">;
  pdfBlob: Blob;
  userId: string; // Clerk user ID
  documentTitle: string;
  documentType?: string;
}

export function EmailComposerSheet({
  isOpen,
  onClose,
  documentId,
  pdfBlob,
  userId,
  documentTitle,
  documentType = "document",
}: EmailComposerSheetProps) {
  const { toast } = useToast();

  // State for email form
  const [emailData, setEmailData] = useState({
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
  });

  const [errors, setErrors] = useState({
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
  });

  const [isSending, setIsSending] = useState(false);

  // Send email action
  const sendEmail = useAction(api.documentEmail.sendDocumentViaResend);

  // Fetch recipient email from document context
  const recipientEmail = useQuery(api.documentEmail.getDocumentRecipientEmail, { documentId });

  // Reset form when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setEmailData({
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
      });
      setErrors({
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
      });
    }
  }, [isOpen]);

  // Auto-populate recipient email when it's fetched
  useEffect(() => {
    if (recipientEmail && !emailData.to) {
      setEmailData((prev) => ({ ...prev, to: recipientEmail }));
    }
  }, [recipientEmail]);

  // Auto-generate subject line when sheet opens
  useEffect(() => {
    if (isOpen && !emailData.subject) {
      const subject = generateEmailSubject(
        { type: documentType, title: documentTitle, metadata: {} },
        "ManageClean OS"
      );
      setEmailData((prev) => ({ ...prev, subject }));
    }
  }, [isOpen, documentType, documentTitle]);

  // Auto-generate default email body when sheet opens
  useEffect(() => {
    if (isOpen && !emailData.body) {
      const defaultBody = generateDefaultEmailBody(documentType, "Valued Client");
      setEmailData((prev) => ({ ...prev, body: defaultBody }));
    }
  }, [isOpen, documentType]);

  // Reset form when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setEmailData({
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
      });
      setErrors({
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
      });
    }
  }, [isOpen]);

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors = {
      to: "",
      cc: "",
      bcc: "",
      subject: "",
      body: "",
    };

    // Validate To field
    if (!emailData.to.trim()) {
      newErrors.to = "Recipient email is required";
    } else if (!validateEmail(emailData.to.trim())) {
      newErrors.to = "Invalid email format";
    }

    // Validate CC field (optional, but must be valid if provided)
    if (emailData.cc.trim() && !validateMultipleEmails(emailData.cc)) {
      newErrors.cc = "Invalid email format (use commas to separate multiple emails)";
    }

    // Validate BCC field (optional, but must be valid if provided)
    if (emailData.bcc.trim() && !validateMultipleEmails(emailData.bcc)) {
      newErrors.bcc = "Invalid email format (use commas to separate multiple emails)";
    }

    // Validate Subject field
    if (!emailData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    // Validate Body field
    if (!emailData.body.trim() || emailData.body === "<p></p>") {
      newErrors.body = "Email body is required";
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  // Handle send email
  const handleSendEmail = async () => {
    // Validate form
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before sending.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    // Check PDF size
    const maxSize = 35 * 1024 * 1024; // 35MB Gmail limit
    if (pdfBlob.size > maxSize) {
      toast({
        title: "File Too Large",
        description: `PDF is ${formatFileSize(pdfBlob.size)}. Gmail maximum is 35MB.`,
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    // Warn about large files
    if (pdfBlob.size > 10 * 1024 * 1024) {
      const confirmed = confirm(
        `This PDF is ${formatFileSize(pdfBlob.size)}. Large files may take longer to send. Continue?`
      );
      if (!confirmed) return;
    }

    setIsSending(true);

    try {
      // Prepare PDF attachment
      const pdfAttachment = await preparePdfAttachment(
        pdfBlob,
        `${documentTitle || "document"}.pdf`
      );

      // Send email via Resend
      const result = await sendEmail({
        to: emailData.to.trim(),
        cc: emailData.cc.trim() || undefined,
        bcc: emailData.bcc.trim() || undefined,
        subject: emailData.subject.trim(),
        body: emailData.body,
        pdfBase64: pdfAttachment.content,
        filename: pdfAttachment.filename,
      });

      if (result.success) {
        toast({
          title: "Email Sent",
          description: `Email sent successfully to ${emailData.to}`,
          duration: 5000,
        });
        onClose();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("[EmailComposerSheet] Error sending email:", error);
      toast({
        title: "Failed to Send Email",
        description:
          error instanceof Error ? error.message : "An error occurred while sending the email",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Email
          </SheetTitle>
          <SheetDescription>Send {documentTitle} via email</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Warning if no recipient email found */}
          {!recipientEmail && recipientEmail !== undefined && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>No Client Email Found</AlertTitle>
              <AlertDescription>
                Please enter the recipient's email address manually.
              </AlertDescription>
            </Alert>
          )}

          {/* To Field */}
          <div className="space-y-2">
            <Label htmlFor="to">
              To <span className="text-red-500">*</span>
            </Label>
            <Input
              id="to"
              type="email"
              placeholder="recipient@example.com"
              value={emailData.to}
              onChange={(e) => {
                setEmailData({ ...emailData, to: e.target.value });
                setErrors({ ...errors, to: "" });
              }}
              className={errors.to ? "border-red-500" : ""}
            />
            {errors.to && <p className="text-sm text-red-500">{errors.to}</p>}
          </div>

          {/* CC Field */}
          <div className="space-y-2">
            <Label htmlFor="cc">CC (optional)</Label>
            <Input
              id="cc"
              type="text"
              placeholder="cc1@example.com, cc2@example.com"
              value={emailData.cc}
              onChange={(e) => {
                setEmailData({ ...emailData, cc: e.target.value });
                setErrors({ ...errors, cc: "" });
              }}
              className={errors.cc ? "border-red-500" : ""}
            />
            {errors.cc && <p className="text-sm text-red-500">{errors.cc}</p>}
          </div>

          {/* BCC Field */}
          <div className="space-y-2">
            <Label htmlFor="bcc">BCC (optional)</Label>
            <Input
              id="bcc"
              type="text"
              placeholder="bcc1@example.com, bcc2@example.com"
              value={emailData.bcc}
              onChange={(e) => {
                setEmailData({ ...emailData, bcc: e.target.value });
                setErrors({ ...errors, bcc: "" });
              }}
              className={errors.bcc ? "border-red-500" : ""}
            />
            {errors.bcc && <p className="text-sm text-red-500">{errors.bcc}</p>}
          </div>

          {/* Subject Field */}
          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subject"
              type="text"
              placeholder="Email subject"
              value={emailData.subject}
              onChange={(e) => {
                setEmailData({ ...emailData, subject: e.target.value });
                setErrors({ ...errors, subject: "" });
              }}
              className={errors.subject ? "border-red-500" : ""}
            />
            {errors.subject && <p className="text-sm text-red-500">{errors.subject}</p>}
          </div>

          {/* Email Body */}
          <div className="space-y-2">
            <Label htmlFor="body">
              Message <span className="text-red-500">*</span>
            </Label>
            <RichTextEditor
              content={emailData.body}
              onChange={(html) => {
                setEmailData({ ...emailData, body: html });
                setErrors({ ...errors, body: "" });
              }}
              placeholder="Write your email message here..."
            />
            {errors.body && <p className="text-sm text-red-500">{errors.body}</p>}
          </div>

          {/* Attachment Info */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2 text-blue-900">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">
                Attachment: {documentTitle}.pdf ({formatFileSize(pdfBlob.size)})
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button onClick={handleSendEmail} disabled={isSending} className="flex-1 gap-2">
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Email
                </>
              )}
            </Button>
            <Button onClick={onClose} variant="outline" disabled={isSending}>
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
